import { Date } from "../date";
import { ArgumentError } from "../errors";
import type { Node } from "../types";
import { ObjectDiff } from "./object-diff";
import { ObjectEquality } from "./object-equality";


export class $Object extends globalThis.Object
{
}

export namespace $Object
{
    export const clone = structuredClone.bind(null);

    export function get<T = any>(node: any, key: string): T;
    export function get<T = any>(node: any, key: string[]): T;
    export function get(node: Node, key: string | string[]): any;
    export function get(node: any, key: string | string[]): any
    {
        const segments =
            (Array.isArray(key))
                ? key
                : parseKey(key);
        return segments.reduce((node, field) => node?.[field], node);
    }

    export function set<T>(node: any, key: string, value: T): T;
    export function set<T>(node: any, key: string[], value: T): T;
    export function set<T>(node: any, key: string | string[], value: T): T
    {
        if (!node)
            throw new ArgumentError(`Invalid node: ${typeof node}.`);

        const segments =
            (Array.isArray(key))
                ? key
                : parseKey(key);
        const last = segments.pop();

        if (!last)
            throw new ArgumentError(`Invalid key: ${key}.`);

        node =
            segments.reduce(
                (node, segment) => node[segment] ?? (node[segment] = {}),
                node
            );

        node[last] = value;
        return node[last];
    }


    export function remove<T>(node: any, key: string): T;
    export function remove<T>(node: any, key: string[]): T;
    export function remove<T>(node: any, key: string | string[]): T
    {
        if (!node)
            throw new ArgumentError(`Invalid node: ${typeof node}.`);

        const segments =
            (Array.isArray(key))
                ? key
                : parseKey(key);
        const last = segments.pop();

        if (!last)
            throw new ArgumentError(`Invalid key: ${key}.`);

        node =
            segments.reduce(
                (node, segment) => node[segment] ?? (node[segment] = {}),
                node
            );

        const value = node[last];
        delete node[last];
        return value;
    }

    export function parseKey(key: string): string[]
    {
        let result = key.split('.');

        result =
            result.map(
                segment =>
                {
                    const indexing = indexedSegment.exec(segment);
                    return (
                        indexing
                            ? [indexing[1], indexing[2]]
                            : segment
                    );
                }
            ).flat();

        return result;
    }

    /**
     * Predicate for comparing two objects by their values
     * 
     * @param obj1 first object to compare
     * @param obj2 second object to compare
     * @param visited Set<[any, any]> tracking compaired pairs of objects for circular reference comparison
     * @returns true if both objects are equal by value, false otherwise
     */
    export function deepEqual<T>(obj1: T, obj2: T): boolean
    {
        return ObjectEquality.areEqual(obj1, obj2);
    }

    export function getDiff<T>(first: T, second: T): string[]
    {
        return ObjectDiff.getDiff(first, second);
    }

    export function containsSubstring(object: any, substring: string, options?: ContainsSubstringOptions): boolean
    {
        if (!object) return false;

        // toString() if primitive
        if (typeof object !== 'object')
        {
            const toString: string = object.toString();
            let value = toString;
            let $substring = substring;

            if (!options?.matchCapitalization)
            {
                value = value.toLocaleLowerCase();
                $substring = $substring.toLocaleLowerCase();
            }

            return value.includes($substring);
        }

        // iteratively recurse if array
        if (Array.isArray(object))
            return object.some(item => containsSubstring(item, substring, options));

        // format if Date
        if (object instanceof globalThis.Date)
        {
            const $substring = options?.matchCapitalization ? substring : substring.toLocaleLowerCase();
            if (options?.dateFormat)
            {
                let value = Date.format(object, options.dateFormat);
                value = options?.matchCapitalization ? value : value.toLocaleLowerCase();
                return value.includes($substring);
            }
            else
            {
                let value = object.toString();
                value = options?.matchCapitalization ? value : value.toLocaleLowerCase();
                return object.toString().includes(substring);
            }
        }

        // iteratively recurse properties if object
        for (const key in object)
        {
            if (options?.propertiesToExclude?.has(key)) continue;
            if (containsSubstring(object[key], substring, options)) return true;
        }

        return false;
    }

    export function containsUndefinedValue(object: any, levels?: number, seen = new WeakSet())
    {
        if (typeof levels === 'number' && levels < 1)
            return false;

        if (typeof object !== 'object' || object === null)
            return false; // Primitive types and null don't need to be checked

        if (seen.has(object))
            return false; // Avoid revisiting the same object to prevent infinite loops
        seen.add(object);

        for (const key in object)
        {
            if (object[key] === undefined)
                return true;

            if (typeof object[key] === 'object' && object[key] !== null)
            {
                if (containsUndefinedValue(object[key], levels && levels - 1, seen))
                    return true;
            }
        }

        return false;
    }

    /**
     * Mimics `Object.assign()` but also deletes keys from `target` that are not present in `source`.
     * 
     * @param target the target object to apply changes to
     * @param source the object containing the desired changes
     * @returns `target` with changes applied from `source` and with keys not present in `source` removed
     */
    export function strictAssign<T extends {}, U extends Object>(target: T, source: U): T
    {
        // Remove keys from target that are not in source
        Object.keys(target).forEach(key =>
        {
            if (!(key in source))
                delete (target as any)[key];
        });

        // Assign and return
        Object.assign(target, source);
        return target;
    }

    const indexedSegment = /^(.+)\[(.+)\]$/;
};

interface ContainsSubstringOptions
{
    dateFormat?: string;
    propertiesToExclude?: Set<string>;
    matchCapitalization?: boolean;
}

export { $Object as Object };

